import { Box, useToast, Skeleton, Flex, useDisclosure } from '@chakra-ui/react';
import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';

import {
  ActiveDiffIcon,
  IconButton,
  InactiveDiffIcon,
} from '~/packages/common-frontend/components/IconButton';
import {
  errorToastConfig,
  errorToastId,
} from '~/packages/common-frontend/constants';
import {
  useGetCreditAgreementDocumentsMetadataQuery,
  useDoesCreditAgreementWipExistQuery,
} from '~/packages/creditAgreements/requests/requests.gql.generated';
import CreditAgreementTable from '~/packages/creditAgreements/ui/CreditAgreementsTable';
import { RemoveCreditAgreementWipButton } from '~/packages/creditAgreements/ui/creditAgreementWip';
import { EditCreditAgreementDocumentWipButton } from '~/packages/creditAgreements/ui/creditAgreementWip//EditCreditAgreementWipButton';
import { CreditAgreementWipDiffViewer } from '~/packages/creditAgreements/ui/creditAgreementWip/CreditAgreementWipDiffViewer';
import { useGetCurrentCaAndWip } from '~/packages/creditAgreements/ui/creditAgreementWip/utils/useGetCurrentCaAndWip';

// Exports
type Props = {
  creditFacilityId: string;
};
export const CreditAgreements = ({ creditFacilityId }: Props) => {
  const toast = useToast();
  const navigate = useNavigate();
  const params = useParams();
  const { implementationId } = params;
  const { isOpen, onToggle } = useDisclosure();

  // Generate path for client routes
  const creditAgreementInfoPathGenerator = (id: string) =>
    `/implementations/${implementationId}/credit-facilities/${creditFacilityId}/credit-agreements/${id}`;

  const {
    loading: creditAgreementDocumentsMetadataLoading,
    data: creditAgreementDocumentsMetadataData,
  } = useGetCreditAgreementDocumentsMetadataQuery({
    variables: {
      creditFacilityId,
    },
    onError: () => {
      if (!toast.isActive(errorToastId)) {
        toast({
          ...errorToastConfig,
          title: `Error loading credit agreements.`,
        });
      }
    },
    fetchPolicy: 'network-only',
  });

  const {
    data: doesCreditAgreementWipExistData,
  } = useDoesCreditAgreementWipExistQuery({
    variables: {
      creditFacilityId,
    },
  });

  const {
    currentCreditAgreementFullArtifact,
    creditAgreementFullArtifactWip,
  } = useGetCurrentCaAndWip({
    creditFacilityId,
    creditAgreementsPath: creditAgreementInfoPathGenerator(''),
  });

  if (
    creditAgreementDocumentsMetadataLoading ||
    !creditAgreementDocumentsMetadataData ||
    !doesCreditAgreementWipExistData
  ) {
    return <Skeleton height="150px" />;
  }

  const doesCreditAgreementWipExist =
    doesCreditAgreementWipExistData?.doesCreditAgreementWipExist ?? false;

  return (
    <Box>
      <Flex justifyContent="flex-end" marginBottom="15px">
        {doesCreditAgreementWipExist && (
          <IconButton
            ariaLabel="info"
            label={isOpen ? 'Hide Changes' : 'Show Changes'}
            icon={isOpen ? <InactiveDiffIcon /> : <ActiveDiffIcon />}
            onClick={() => onToggle()}
          />
        )}
        <RemoveCreditAgreementWipButton
          showDiscardWipButton
          creditFacilityId={creditFacilityId}
          doesCreditAgreementWipExist={doesCreditAgreementWipExist}
          onSuccess={() =>
            navigate(
              `/implementations/${implementationId}/credit-facilities/${creditFacilityId}/credit-agreements`,
            )
          }
        />
        {currentCreditAgreementFullArtifact && (
          <EditCreditAgreementDocumentWipButton
            wipPath={`/implementations/${implementationId}/credit-facilities/${creditFacilityId}/credit-agreements/wip`}
          />
        )}
      </Flex>
      <CreditAgreementTable
        creditAgreementDocumentsMetadata={
          creditAgreementDocumentsMetadataData.creditAgreementDocumentsMetadata
        }
        creditAgreementInfoPathGenerator={creditAgreementInfoPathGenerator}
      />
      {isOpen &&
        currentCreditAgreementFullArtifact &&
        creditAgreementFullArtifactWip && (
          <CreditAgreementWipDiffViewer
            currentCreditAgreementFullArtifact={
              currentCreditAgreementFullArtifact
            }
            creditAgreementFullArtifactWip={creditAgreementFullArtifactWip}
          />
        )}
    </Box>
  );
};
